import React, { useRef } from 'react';
import styles from './O_CapsuleCommentsPost.module.css';
import cn from 'classnames';
import { MockPost } from '@/mocks';
import { ScenarioReaction } from '@/services/capsulesApi';
import A_UserAvatar from '@/components/A_Atoms/A_UserAvatar/A_UserAvatar';
import A_TextArea from '@/components/A_Atoms/A_TextArea/A_TextArea';
import { useTranslation } from '@/hooks/useTranslation';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import A_Indicator from '@/components/A_Atoms/A_Indicator/A_Indicator';
import Q_ScrollBar from '@/components/Q_Quarks/Q_ScrollBar/Q_ScrollBar';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';

interface O_CapsuleCommentsPostProps {
  capsule: MockPost;
  comment?: ScenarioReaction | null;
  commentCount: number;
  plantCount: number;
  isAuthenticated: boolean;
  canComment: boolean;
  commentDraft: string;
  onCommentDraftChange: (value: string) => void;
  onCommentSubmit: () => void;
  canReply: boolean;
  replyDraft: string;
  onReplyDraftChange: (value: string) => void;
  onReplySubmit: () => void;
}

const formatDateValue = (dateString?: string) => {
  if (!dateString) return '';
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

export default function O_CapsuleCommentsPost({
  capsule,
  comment,
  commentCount,
  plantCount,
  isAuthenticated,
  canComment,
  commentDraft,
  onCommentDraftChange,
  onCommentSubmit,
  canReply,
  replyDraft,
  onReplyDraftChange,
  onReplySubmit,
}: O_CapsuleCommentsPostProps) {
  const t = useTranslation();
  const mainComment = comment ?? null;
  const isGuestComment = Boolean(mainComment?.isGuest);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const renderCommentForm = () => {
    if (!isAuthenticated || !canComment) return null;

    const handleSubmit = () => {
      if (!commentDraft.trim()) return;
      onCommentSubmit();
    };

    return (
      <div className={styles.form_wrapper}>
        <A_TextArea
          label={t('capsuleReactions.commentLabel')}
          value={commentDraft}
          onChange={onCommentDraftChange}
          background='gray'
          height={120}
        />
        <A_CircleButton
          icon={<Q_Icon.SendIcon width={24} height={24} fill='none' />}
          size='large'
          variant='secondary'
          onClick={handleSubmit}
        />
      </div>
    );
  };

  const renderReplyControls = () => {
    if (!mainComment || !isAuthenticated || isGuestComment || !canReply) {
      return null;
    }

    return (
      <div className={styles.form_wrapper}>
        <A_TextArea
          label={t('capsuleReactions.replyLabel')}
          value={replyDraft}
          onChange={onReplyDraftChange}
          background='gray'
          height={120}
        />
        <A_CircleButton
          icon={<Q_Icon.SendIcon width={24} height={24} fill='none' />}
          size='large'
          onClick={onReplySubmit}
          variant='secondary'
        />
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.inner_header}>
        <span className={cn(styles.inner_header_text, 'text-p-7')}>
          {capsule.title}
        </span>
        <div className={styles.indicators_wrapper}>
          <A_Indicator
            count={commentCount}
            icon={
              <Q_Icon.CommentIndicatorIcon width={24} height={24} fill='none' />
            }
          />
        </div>
      </div>

      <div className={styles.scroll_wrapper}>
        <div ref={scrollContainerRef} className={styles.scroll_container}>
          {mainComment && (
            <div className={styles.comment_details_wrapper}>
              <div className={styles.comment_author_wrapper}>
                <div className={styles.comment_inner_wrapper}>
                  <div className={styles.comment_author_info_wrapper}>
                    <A_UserAvatar
                      src={mainComment.author.avatar}
                      height={32}
                      width={32}
                    />
                    <span
                      className={cn(styles.comment_author_name, 'text-p-7')}
                    >
                      {mainComment.author.name}
                    </span>
                  </div>
                  <span className={cn(styles.comment_date, 'text-p-7')}>
                    {formatDateValue(mainComment.createdAt)}
                  </span>
                </div>
                <p className={cn(styles.comment_text, 'text-p-2')}>
                  {mainComment.text || t('capsuleReactions.noCommentYet')}
                </p>
              </div>

              {!!mainComment.replies?.length && (
                <div className={styles.replies_wrapper}>
                  {mainComment.replies.map(reply => (
                    <div key={reply.id} className={styles.reply_wrapper}>
                      <div className={styles.reply_inner_wrapper}>
                        <div className={styles.reply_author_info_wrapper}>
                          <A_UserAvatar
                            src={reply.author.avatar}
                            height={28}
                            width={28}
                          />
                          <span
                            className={cn(styles.reply_author_name, 'text-p-7')}
                          >
                            {reply.author.name}
                          </span>
                        </div>
                        <span className={cn(styles.reply_date, 'text-p-7')}>
                          {formatDateValue(reply.createdAt)}
                        </span>
                      </div>
                      <p className={cn(styles.reply_text, 'text-p-2')}>
                        {reply.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {renderReplyControls()}
            </div>
          )}
          {renderCommentForm()}
        </div>
        <Q_ScrollBar
          containerRef={scrollContainerRef}
          className={styles.scrollbar}
        />
      </div>
    </div>
  );
}
